import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kmsService } from '@aaas/shared';

interface UseWorkspaceIntegrationQueryOptions {
  idToken: string | undefined;
  enabled?: boolean;
}

/**
 * React Query hook for fetching workspace-level Slack integration
 *
 * Fetches workspace-level integration (space_id = NULL)
 */
export const useWorkspaceSlackIntegrationQuery = ({
  idToken,
  enabled = true,
}: UseWorkspaceIntegrationQueryOptions) => {
  return useQuery({
    queryKey: ['kms-workspace-integrations', 'slack'],
    queryFn: async () => {
      if (!idToken) {
        throw new Error('Authentication token required');
      }

      const response = await kmsService.getWorkspaceSlackIntegration(idToken);
      return response.data;
    },
    enabled: enabled && !!idToken,
    staleTime: 30000, // Fresh for 30 seconds
    refetchOnWindowFocus: true,
  });
};

interface UseWorkspaceSlackIntegrationByIdQueryOptions {
  integrationId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
}

/**
 * React Query hook for fetching a specific workspace-level Slack integration by ID
 *
 * Used by the detail page to display full integration information
 */
export const useWorkspaceSlackIntegrationByIdQuery = ({
  integrationId,
  idToken,
  enabled = true,
}: UseWorkspaceSlackIntegrationByIdQueryOptions) => {
  return useQuery({
    queryKey: ['kms-workspace-integration', integrationId],
    queryFn: async () => {
      if (!idToken || !integrationId) {
        throw new Error('Integration ID and authentication token required');
      }

      const response = await kmsService.getWorkspaceSlackIntegrationById(idToken, integrationId);
      return response.data;
    },
    enabled: enabled && !!idToken && !!integrationId,
    staleTime: 30000, // Fresh for 30 seconds
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });
};

/**
 * React Query mutation hook for getting workspace-level Slack OAuth URL
 */
export const useWorkspaceSlackOAuthUrl = () => {
  return useMutation({
    mutationFn: async ({ idToken, workspaceId }: { idToken: string; workspaceId: string }) => {
      const response = await kmsService.getWorkspaceSlackOAuthUrl(idToken, workspaceId);
      return response.data;
    },
  });
};

/**
 * React Query mutation hook for disconnecting Slack integration (workspace-level)
 */
export const useDisconnectSlackIntegrationWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ idToken, integrationId }: { idToken: string; integrationId: string }) => {
      return await kmsService.disconnectSlackIntegrationWorkspace(idToken, integrationId);
    },
    onSuccess: () => {
      // Invalidate workspace-level Slack integration query
      queryClient.invalidateQueries({ queryKey: ['kms-workspace-integrations', 'slack'] });
    },
  });
};
